/**
 * @fileoverview Notification Center Context for managing persistent user notifications.
 *
 * This context provides functionality for:
 * - Fetching and managing persistent notifications from the backend
 * - Real-time notification updates
 * - Mark as read/dismiss functionality
 * - Unread count tracking
 * - Notification filtering by category
 *
 * Different from NotificationContext which handles temporary toast/snackbar notifications.
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, isAppPausedError } from './AuthContext';
import { useUserActivity } from '../hooks/useUserActivity';

/**
 * Inactivity timeout in milliseconds (10 minutes)
 * When user is inactive for this long, notification polling is paused
 */
const INACTIVITY_TIMEOUT = 10 * 60 * 1000;

const NotificationCenterContext = createContext();

/**
 * Custom hook to access the notification center context
 * @returns {Object} Notification center context value
 */
export const useNotificationCenter = () => {
  const context = useContext(NotificationCenterContext);
  if (!context) {
    throw new Error('useNotificationCenter must be used within a NotificationCenterProvider');
  }
  return context;
};

/**
 * Notification Center Provider Component
 * Manages persistent notifications from the backend
 */
export const NotificationCenterProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isFullInboxOpen, setIsFullInboxOpen] = useState(false);
  const pollIntervalRef = useRef(null);
  const { apiRequest } = useAuth();

  // Track user activity - pause polling when user is inactive
  const { isActive: isUserActive } = useUserActivity(INACTIVITY_TIMEOUT);

  /**
   * Fetch notifications from the backend
   */
  const fetchNotifications = useCallback(async (category = null, isRead = null) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (isRead !== null) params.append('is_read', isRead);

      const url = `/api/notifications${params.toString() ? '?' + params.toString() : ''}`;
      const response = await apiRequest(url);

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      if (!isAppPausedError(error)) {
        if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching notifications:', error);
      }
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest]);

  /**
   * Fetch only unread count (lightweight for polling)
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await apiRequest('/api/notifications/unread-count');
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      if (!isAppPausedError(error)) {
        if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching unread count:', error);
      }
      }
    }
  }, [apiRequest]);

  /**
   * Mark a notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await apiRequest(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_read: true })
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, is_read: true } : n
          )
        );

        // Decrement unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking notification as read:', error);
      }
    }
  }, [apiRequest]);

  /**
   * Dismiss a notification
   */
  const dismissNotification = useCallback(async (notificationId) => {
    try {
      const response = await apiRequest(`/api/notifications/${notificationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_dismissed: true })
      });

      if (response.ok) {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));

        // Update unread count if it was unread
        const notification = notifications.find(n => n.id === notificationId);
        if (notification && !notification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error dismissing notification:', error);
      }
    }
  }, [apiRequest, notifications]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async (category = null) => {
    try {
      const url = category ? `/api/notifications/mark-all-read?category=${category}` : '/api/notifications/mark-all-read';
      const response = await apiRequest(url, { method: 'POST' });

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => ({ ...n, is_read: true }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error marking all as read:', error);
      }
    }
  }, [apiRequest]);

  /**
   * Dismiss all notifications
   */
  const dismissAll = useCallback(async (category = null) => {
    try {
      const url = category ? `/api/notifications/dismiss-all?category=${category}` : '/api/notifications/dismiss-all';
      const response = await apiRequest(url, { method: 'POST' });

      if (response.ok) {
        // Clear local state
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error dismissing all notifications:', error);
      }
    }
  }, [apiRequest]);

  /**
   * Delete a notification
   */
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      const response = await apiRequest(`/api/notifications/${notificationId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));

        // Update unread count if it was unread
        const notification = notifications.find(n => n.id === notificationId);
        if (notification && !notification.is_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error deleting notification:', error);
      }
    }
  }, [apiRequest, notifications]);

  /**
   * Toggle notification center open/closed
   */
  const toggleOpen = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  /**
   * Close notification center
   */
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Open full inbox modal
   */
  const openFullInbox = useCallback(() => {
    setIsOpen(false); // Close dropdown first
    setIsFullInboxOpen(true);
  }, []);

  /**
   * Close full inbox modal
   */
  const closeFullInbox = useCallback(() => {
    setIsFullInboxOpen(false);
  }, []);

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /**
   * Poll for new notifications every 15 minutes (reduced to allow Neon DB auto-suspend)
   * Pauses when user is inactive to reduce unnecessary server load
   */
  useEffect(() => {
    // Don't poll if user is inactive
    if (!isUserActive) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Fetch immediately when user becomes active again
    fetchUnreadCount();

    // Poll for unread count
    pollIntervalRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 900000); // 15 minutes

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [fetchUnreadCount, isUserActive]);

  /**
   * Refresh notifications when notification center is opened
   */
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  /**
   * Refresh both notifications and count (useful after creating new notifications)
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount()
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    isLoading,
    isOpen,
    isFullInboxOpen,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    dismissNotification,
    markAllAsRead,
    dismissAll,
    deleteNotification,
    toggleOpen,
    close,
    openFullInbox,
    closeFullInbox,
    refresh
  };

  return (
    <NotificationCenterContext.Provider value={value}>
      {children}
    </NotificationCenterContext.Provider>
  );
};

export default NotificationCenterContext;
